import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import { Card, Col, Container, Row } from "reactstrap";
import { H2 } from "../../../../AbstractElements";
import { ShowDangerAlert } from "../../../../Component/BonusUi/SweetAlert/ShowDangerAlert/ShowDangerAlert";
import { useAlerts } from "../../../../hooks/useAlert";
import { employeeUpdateProbationModal } from "../../../../utils/Constant";
import useIsMobile from "../../../../hooks/useInMobile";
import { Nullable } from "../../../../Types/Common";
import { closeModal } from "../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import {
  clearEmployeeUpdateProbationError,
  clearEmployeeUpdateProbationSuccess,
} from "../../../hrZone/EmployeeProbation/redux/slice/EmployeeUpdateProbationSlice";
import { ShowSuccessAlert } from "../../../../Component/BonusUi/SweetAlert/ShowSuccessAlert";
import { useModalSelector } from "../../../../hooks/useModalSelector";
import {
  clearTeamMembersListStates,
} from "../redux/selector/teamMembersListSelector";
import TeamMembersTable from "./table/TeamMembersTable";
import TeamMembersCard from "./cards/TeamMembersCard";
import ViewBtn from "./actionButtons/ViewBtn";
import UpdateProbationModal from "../../../hrZone/EmployeeProbation/component/modal/UpdateProbationModal";
import { useTeamMembersListQuery } from "../../../../ReduxToolkit/api/teamMembers";
import { useEmployeeProfile } from "../../../../hooks/useEmployeeProfile";
import { showApiResultAlert } from "../../../../utils/AlertManager/showApiAlert";
import { fetchCenterListAsyncThunk } from "../../../../ReduxToolkit/Slice/CenterListSlice";
import { fetchDepartmentAsyncThunk } from "../../../departmentList/redux/slice/DepartmentListSlice";
import { fetchEmployeesListThunk } from "../../../hrZone/Users/redux/slice/EmployeesListSlie";
import Loader from "../../../../Layout/Loader/Loader";
import { fetchDesignations } from "../../../designation/redux/slice/designationListSlice";

const getTeamMembersAlertConfig = (updateProbationError: string) => [
  {
    textMessage: updateProbationError,
    alertFunc: ShowDangerAlert,
    clearActions: [clearEmployeeUpdateProbationError],
  },
];

const ViewTeamMembers = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [toggle, setToggle] = useState<boolean>(true);
  const isMobile = useIsMobile();
  const [selectedUser, setSelectedUser] = useState<Nullable<any>>(null);

  const { employeeProfileDetails, loggedInEmployee } = useEmployeeProfile();

  const isEmployeeProfilePath = location.pathname.includes(
    "/user-management/user"
  );

  const employeeId = employeeProfileDetails?.jobInformation?.userId!;
  const {
    data: teamMembers,
    isLoading: isTeamMembersListLoading,
    isFetching: isTeamMembersListFetching,
    isError: isTeamMembersListError,
    error: teamMembersErrors,
    refetch: refetchTeamMembers,
  } = useTeamMembersListQuery(isEmployeeProfilePath ? employeeId : undefined);

  const probationMembers = teamMembers?.data?.probation_members ?? [];

  const hasProbationMembers = probationMembers && probationMembers.length > 0;
  const teamMembersList = teamMembers?.data?.team_members ?? [];

  const { modalType } = useModalSelector();

  const {
    data: employeeUpdateProbationDetails,
    error: employeeUpdateProbationError,
    loading: isProbationUpdating,
  } = useAppSelector(({ employeeProbationUpdate }) => employeeProbationUpdate);

  const { isOpen } = useAppSelector(
    ({ employeeProfileDetailModalSlice }) => employeeProfileDetailModalSlice
  );

  const { data: surveyFormQuestionDetails } = useAppSelector(
    ({ employeeSurveyFormResponse }) => employeeSurveyFormResponse
  );

  const isLoading = isProbationUpdating;
  const alertConfig = useMemo(
    () => getTeamMembersAlertConfig(employeeUpdateProbationError),
    [employeeUpdateProbationError]
  );

  useAlerts(alertConfig);

  const handleToggleModal = () => {
    setSelectedUser(null);
    dispatch(closeModal());
  };

  const handleConfirm = () => {
    setSelectedUser(null);
    dispatch(clearEmployeeUpdateProbationSuccess());
    refetchTeamMembers();
    dispatch(closeModal());
  };

  // Fetch team members on component mount
  useEffect(() => {
    return () => clearTeamMembersListStates(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (employeeUpdateProbationDetails?.success) {
      ShowSuccessAlert({
        text: employeeUpdateProbationDetails.message,
        onConfirm: handleConfirm,
      });
    }
  }, [employeeUpdateProbationDetails]);

  useEffect(() => {
    if (isTeamMembersListError) {
      showApiResultAlert(teamMembersErrors, {
        title: `Hey ${loggedInEmployee?.name}!`,
      });
    }
  }, [isTeamMembersListError]);

  useEffect(() => {
    if (
      isOpen &&
      surveyFormQuestionDetails &&
      surveyFormQuestionDetails?.form_details &&
      surveyFormQuestionDetails?.form_details?.linked_entities &&
      surveyFormQuestionDetails?.form_details?.linked_entities?.length > 0
    ) {
      if (
        surveyFormQuestionDetails?.form_details?.linked_entities.includes(
          "users"
        )
      ) {
        dispatch(fetchEmployeesListThunk({ manager: 1 }));
      }
      if (
        surveyFormQuestionDetails?.form_details?.linked_entities.includes(
          "departments"
        )
      ) {
        dispatch(fetchDepartmentAsyncThunk({ status: "active" }));
      }
      if (
        surveyFormQuestionDetails?.form_details?.linked_entities.includes(
          "locations"
        )
      ) {
        dispatch(fetchCenterListAsyncThunk());
      }
      if (
        surveyFormQuestionDetails?.form_details?.linked_entities.includes(
          "designation"
        )
      ) {
        dispatch(fetchDesignations());
      }
    }
  }, [
    isOpen,
    modalType,
    surveyFormQuestionDetails?.form_details?.linked_entities?.length,
    dispatch,
  ]);

  return (
    <Container fluid className="pt-4">
      {isLoading && <Loader />}
      <Row
        className={`align-items-center justify-content-${
          toggle || isMobile ? "end" : "end"
        } mb-2`}
      >
        {!isMobile && toggle && (
          <Col lg="4" className="d-flex justify-content-end">
            <ViewBtn onClick={() => setToggle(false)} label="Table View" />
          </Col>
        )}
      </Row>

      {/* CARD VIEW */}
      {(toggle || isMobile) && (
        <>
          {hasProbationMembers && (
            <Row className="mb-2">
              <Col xs="12">
                <H2 className="mb-3 f-16">Probation Members</H2>
              </Col>
              {probationMembers.map((member) => (
                <TeamMembersCard
                  key={member.id}
                  dispatch={dispatch}
                  type="probationMember"
                  teamMembersDetails={member}
                  loading={isTeamMembersListLoading}
                  setSelectedUser={setSelectedUser}
                />
              ))}
            </Row>
          )}

          {/* Team Members Card View */}
          {teamMembersList && (
            <Row className="mb-2">
              <Col xs="12">
                <H2 className="mb-3 f-16">Team Members</H2>
              </Col>
              {teamMembersList.map((memberData) => (
                <TeamMembersCard
                  type="teamMember"
                  dispatch={dispatch}
                  key={memberData.id}
                  teamMembersDetails={memberData}
                  loading={
                    isTeamMembersListLoading || isTeamMembersListFetching
                  }
                />
              ))}
            </Row>
          )}
        </>
      )}

      {/* TABLE VIEW */}
      {!toggle && (
        <>
          {hasProbationMembers && (
            <Card className="px-4 pb-4 pt-3 d-none d-lg-block mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <H2 className="mb-2 f-16">Probation Members</H2>
                <ViewBtn label="Card View" onClick={() => setToggle(true)} />
              </div>
              <Row>
                <TeamMembersTable
                  type="probationMember"
                  data={probationMembers}
                  dispatch={dispatch}
                  setSelectedUser={setSelectedUser}
                />
              </Row>
            </Card>
          )}

          {teamMembersList && (
            <Card className="px-4 pb-4 pt-3 d-none d-lg-block">
              <div className="d-flex justify-content-between align-items-center">
                <H2 className="mb-2 f-16">Team Members</H2>
                {!hasProbationMembers && (
                  <ViewBtn label="Card View" onClick={() => setToggle(true)} />
                )}
              </div>
              <Row>
                <TeamMembersTable
                  type="teamMember"
                  data={teamMembersList}
                  dispatch={dispatch}
                />
              </Row>
            </Card>
          )}
        </>
      )}
      {modalType === employeeUpdateProbationModal && (
        <UpdateProbationModal
          selectedUser={selectedUser}
          handleToggle={handleToggleModal}
        />
      )}
    </Container>
  );
};

export default ViewTeamMembers;
